<template>
    <div ref="cf" class="column-filter" @keyup.escape="show = false">
        <button class="column-filter-button" @click="show = !show" :class="active ? 'filter-active' : ''"
            title="Filter">
            <font-awesome-icon icon="filter" fixed-width /></button>

        <div v-if="show" ref="cff" class="column-filter-form" :class="type !== 'datetime' ? 'scrollable' : ''">
            <div class="column-filter-form__header">
                <button @click="doClear">Clear</button>
                <button @click="doFilter" :disabled="!acceptableInput">Apply</button>
            </div>

            <!-- datetime -->
             <div v-if="type === 'datetime'">
                <a href="#" @click="filterToday">Today</a>&nbsp;&nbsp;<a href="#" @click="filterSevenDays">Last 7 days</a>&nbsp;&nbsp;<a href="#" @click="filterThirtyDays">Last 30 days</a>
                <div class="one-column-layout">
                    <date-picker mode="date" :popover="popoverOptions" v-model="filterRangeStart"
                        :input-debounce="500"
                        :model-config="dateConfig" :max-date="new Date().setDate(new Date().getDate())"
                        class="three-column-layout">
                        <template #default="{ inputValue, inputEvents, togglePopover }">
                            <label>From</label>
                            <input ref="cfDateInput" type="text" :value="inputValue" v-on="inputEvents" />
                            <button @click="togglePopover"><font-awesome-icon icon="calendar" fixed-width></font-awesome-icon></button>
                        </template>
                    </date-picker>
                    <date-picker mode="date" :popover="popoverOptions" v-model="filterRangeEnd"
                        :input-debounce="500"
                        :model-config="dateConfig2" :max-date="new Date().setDate(new Date().getDate())"
                        class="three-column-layout">
                        <template #default="{ inputValue, inputEvents, togglePopover }">
                            <label>To</label>
                            <input type="text" :value="inputValue" v-on="inputEvents" />
                            <button @click="togglePopover"><font-awesome-icon icon="calendar" fixed-width></font-awesome-icon></button>
                        </template>
                    </date-picker>
                </div>
            </div>

            <!-- category -->
            <div v-else-if="type === 'category'" class="category-form-layout">
                <input ref="selectAll" id="select-all" type="checkbox" @click="selectAllOrNone" checked="true">
                <label for="select-all">(Select all)</label>

                <template v-for="(category, index) in categoriesMap">
                    <input :id="`category_option_${index}`" type="checkbox" v-model="category.selected"
                        @change="categoryChanged(category)">
                    <label :for="`category_option_${index}`">{{ category.label }}</label>
                </template>
            </div>

            <!-- number -->
            <div v-else-if="type === 'number'" class="one-column-layout">
                <select v-model="filterOperator">
                    <option v-for="option in options" :value="option.key">{{ option.label }}</option>
                </select>
                <units-input v-if="units" ref="cfInput" type="number" v-model="filterValue" :units="units" @keyup.enter="doFilter"></units-input>
                <input v-else ref="cfInput" type="number" v-model="filterValue" @keyup.enter="doFilter"></input>
            </div>

            <!-- text -->
            <div v-else class="one-column-layout">
                <select v-model="filterOperator">
                    <option v-for="option in options" :value="option.key">{{ option.label }}</option>
                </select>
                <input ref="cfInput" type="text" v-model="filterValue" @keyup.enter="doFilter">
            </div>
        </div>
    </div>
</template>
<script>
import UnitsInput from './Actions/UnitsInput.vue';

const typeOptions = {
    'text': [{ key: 'icontains', label: 'contains' }, { key: 'iexact', label: 'matches' }],
    'number': [
        { key: 'lt', label: 'is less than' },
        { key: 'lte', label: 'is less than or equal to' },
        { key: 'eq', label: 'is equal to' },
        { key: 'neq', label: 'is not equal to' },
        { key: 'gte', label: 'is greater than or equal to' },
        { key: 'gt', label: 'is greater than' },
    ],
    'boolean': [{ key: 'true', label: 'yes' }, { key: 'false', label: 'no' }],
    'category': [{ key: 'in', label: 'includes' }],
    'datetime': [{ key: 'after', label: 'after' }, { key: 'before', label: 'before' }],
    'default': [{ key: 'icontains', label: 'contains' }, { key: 'iexact', label: 'matches' }],
};

export default {
    name: 'ColumnFilter',
    components: {
        UnitsInput,
    },
    props: {
        columnId: String,
        filter: Array,
        type: String,
        categories: { type: Array, default: () => [] },
        units: Array,
    },
    data() {
        return {
            active: false,
            show: false,
            categoriesMap: [],
            filterOperator: null,
            filterValue: null,
            filterRangeStart: null,
            filterRangeEnd: null,
            dateConfig: {
                type: 'string',
                mask: 'iso',
                timeAdjust: '00:00:00',
            },
            dateConfig2: {
                type: 'string',
                mask: 'iso',
                timeAdjust: '00:00:00',
            },
            popoverOptions: { visibility: 'click' },
        }
    },
    mounted() {
        this.$nextTick(() => {
            if (!this.active) {
                this.filterOperator = this.options[0].key;
            }
        });
    },
    methods: {
        selectAllOrNone() {
            const selectAll = this.$refs.selectAll.checked;
            this.categoriesMap.forEach(element => element.selected = selectAll);
        },
        categoryChanged() {
            if (this.categoriesMap.length === 0) return;
            // If all categories are checked then also check 'select all'
            const selectionCount = this.categoriesMap.filter(e => e.selected).length;
            if (selectionCount === this.categories.length) {
                this.$refs.selectAll.checked = true;
                this.$refs.selectAll.indeterminate = false;
            }
            else if (selectionCount === 0) {
                this.$refs.selectAll.checked = false;
                this.$refs.selectAll.indeterminate = false;
            }
            else {
                this.$refs.selectAll.checked = false;
                this.$refs.selectAll.indeterminate = true;
            }

            this.filterValue = this.categoriesMap.filter(e => e.selected).map(e => e.key).join(',');
        },
        clickedSomewhere(event) {
            const withinThisComponent = event.composedPath().includes(this.$refs.cf);
            if (!withinThisComponent) {
                this.show = false;
            }
        },
        positionDropDown() {
            const table = document.querySelector('.data-table__table-wrapper');
            const th = this.$el.parentElement.parentElement;
            const button = this.$el;
            const dropDown = this.$refs.cff;

            // console.log("table.offsetLeft", table.offsetLeft)
            // console.log("table.clientLeft", table.clientLeft)
            // console.log("table.clientWidth", table.clientWidth)
            // console.log("table.scrollLeft", table.scrollLeft)
            // console.log("th.offsetLeft", th.offsetLeft)
            // console.log("th.clientWidth", th.clientWidth)
            // console.log("dropDown.clientWidth", dropDown.clientWidth)

            const alignLeft = () => {
                // Align the left of the drop down with left of the th
                const leftOffset = table.scrollLeft + button.offsetLeft;
                dropDown.style.transform = 'translate(-' + leftOffset + 'px, 2px)';
                dropDown.classList.add('drop-down-left-align');
            };

            const alignRight = () => {
                // Align the right of the drop down with right of the th
                const rightOffset = button.offsetLeft + dropDown.clientWidth - th.clientWidth;
                dropDown.style.transform = 'translate(-' + rightOffset + 'px, 2px)';
                dropDown.classList.add('drop-down-right-align');
            };

            // If there is not enough space to the left before we overflow the start of the table,
            // then align with the left side of the column header instead.
            // Otherwise, align the right side of the drop down with the right side of the column header. This puts the drop down next to the button that activated it.

            // console.log("START SAUSAGE", th.offsetLeft + th.clientWidth)
            // console.log("START HOLE", table.scrollLeft + dropDown.clientWidth)
            if (th.offsetLeft + th.clientWidth < table.scrollLeft + dropDown.clientWidth) {
                alignLeft();
            }
            else {
                alignRight();
            }

            // This is the opposite test, i.e. align to the left unless there is not enough space
            // at the end of the table.
            // console.log("END SAUSAGE", table.clientWidth + table.scrollLeft)
            // console.log("END HOLE", th.offsetLeft + dropDown.clientWidth)
            // if (table.clientWidth + table.scrollLeft < th.offsetLeft + dropDown.clientWidth) {
            //     alignRight();
            // }
            // else {
            //     alignLeft();
            // }

            // Style the parent column header.
            th.style.backgroundColor = 'white';
        },
        doFilter() {
            this.show = false;
            this.active = true;
            if (this.type === 'datetime') {
                let filterValue = [];
                if (this.filterRangeStart) {
                    filterValue.push({ operator: 'after', value: this.filterRangeStart });
                }
                if (this.filterRangeEnd) {
                    let dt = new Date(this.filterRangeEnd);
                    dt.setDate(dt.getDate() + 1)
                    filterValue.push({ operator: 'before', value: dt.toISOString() });
                }
                this.$emit('filterChanged', this.columnId, filterValue);
            }
            else if (this.type === 'category') {
                if (this.$refs.selectAll.indeterminate) {
                    this.$emit('filterChanged', this.columnId, [
                        { operator: this.filterOperator, value: this.filterValue }
                    ]);
                }
                else {
                    this.$emit('filterChanged', this.columnId, []);
                    this.active = false;
                }
            }
            else if (this.type === 'number') {
                if (this.units) {
                    this.$emit('filterChanged', this.columnId, [
                        { operator: this.filterOperator, value: this.filterValue.value, unit: this.filterValue.unit }
                    ]);
                }
                else {
                    this.$emit('filterChanged', this.columnId, [
                        { operator: this.filterOperator, value: this.filterValue }
                    ]);
                }
            }
            else {
                this.$emit('filterChanged', this.columnId, [
                    { operator: this.filterOperator, value: this.filterValue }
                ]);
            }
        },
        doClear() {
            this.show = false;
            this.active = false;
            this.filterOperator = this.options[0].key;
            this.filterValue = null;
            this.filterRangeStart = null;
            this.filterRangeEnd = null;
            this.categoriesMap.forEach(category => category.selected = true);
            this.$emit('filterChanged', this.columnId, []);
        },
        updateCategoryMap() {
            const makeLabel = function (value) {
                let label = value.toString().replaceAll('_', ' ');
                return label.charAt(0).toUpperCase() + label.substring(1);
            };

            let m = [];
            for (const category of this.categories) {
                if (typeof category === 'string' || typeof category === 'number') {
                    m.push({ key: category, label: makeLabel(category), selected: true });
                }
                else {
                    if (Object.hasOwn(category, 'label')) {
                        m.push({ key: category.key, label: category.label, selected: true });
                    }
                    else {
                        m.push({ key: category.key, label: makeLabel(category.key), selected: true });
                    }
                }
            }
            this.categoriesMap = m;
        },
        filterToday() {
            this.filterRangeStart = new Date();
            this.filterRangeEnd = new Date();
        },
        filterSevenDays() {
            this.filterRangeStart = new Date().setDate(new Date().getDate() - 7);
            this.filterRangeEnd = new Date();
        },
        filterThirtyDays() {
            this.filterRangeStart = new Date().setDate(new Date().getDate() - 30);
            this.filterRangeEnd = new Date();
        },
    },
    computed: {
        acceptableInput() {
            if (this.filterOperator === null) return false;

            if (this.type === 'datetime') {
                return this.filterRangeStart || this.filterRangeEnd;
            }
            else if (this.type === 'category') {
                return this.categoriesMap.some(category => category.selected);
            }
            else if (this.type === 'number') {
                return this.filterValue !== null;
            }
            else {
                return this.filterValue && this.filterValue.length > 0;
            }
        },
        options() {
            if (Object.hasOwn(typeOptions, this.type)) return typeOptions[this.type];
            return typeOptions.default;
        },
    },
    watch: {
        show: function () {
            if (this.show) {
                this.updateCategoryMap();
                this.$nextTick(() => {
                    // Set initial drop down position and when scrolled.
                    this.positionDropDown();
                    const table = document.querySelector('.data-table__table-wrapper');
                    table.addEventListener('scroll', this.positionDropDown);
                    window.addEventListener('resize', this.positionDropDown);

                    // Listen for click events outside of the form (which closes the form.)
                    document.addEventListener('click', this.clickedSomewhere, { capture: true });

                    // Set focus.
                    if (this.type === 'datetime') {
                        this.$refs.cfDateInput.focus();
                    }
                    else if (this.type === 'text' || this.type === 'number') {
                        this.$refs.cfInput.focus();
                    }
                    this.categoryChanged();
                });
            }
            else {
                const table = document.querySelector('.data-table__table-wrapper');
                const th = this.$el.parentElement.parentElement;
                const dropDown = this.$refs.cff;

                th.style.backgroundColor = 'buttonface';
                document.removeEventListener('click', this.clickedSomewhere, { capture: true });
                table.removeEventListener('scroll', this.positionDropDown);
                window.removeEventListener('resize', this.positionDropDown);
                dropDown.classList.remove('drop-down-left-align');
                dropDown.classList.remove('drop-down-right-align');
            }
        },
        filter: {
            handler(newValue) {
                if (newValue === null) {
                    this.doClear();
                }
                else {
                    if (this.type === 'datetime') {
                        for (const filter of newValue) {
                            if (filter.operator === 'after') {
                                this.filterRangeStart = filter.value;
                            }
                            else if (filter.operator === 'before') {
                                this.filterRangeEnd = filter.value;
                            }
                        }
                    }
                    else if (this.type === 'category') {
                        this.filterOperator = newValue[0].operator;
                        this.filterValue = newValue[0].value;
                        this.updateCategoryMap();
                        const newCats = newValue[0].value.split(',');
                        this.categoriesMap.forEach(c => {
                            c.selected = newCats.includes(c.key);
                        });
                    }
                    else if (this.type === 'number') {
                        if (this.units) {
                            this.filterOperator = newValue[0].operator;
                            this.filterValue = { value: newValue[0].value, unit: newValue[0].unit };
                        }
                        else {
                            this.filterOperator = newValue[0].operator;
                            this.filterValue = newValue[0].value;
                        }
                    }
                    else {
                        this.filterOperator = newValue[0].operator;
                        this.filterValue = newValue[0].value;
                    }
                    this.active = true;
                }
            },
            immediate: true,
        },
    },
}
</script>
<style scoped>
.column-filter-form {
    position: absolute;
    width: 280px;
    max-height: 55vh;
    padding: 16px;
    background-color: white;
    box-shadow: rgba(0, 0, 0, 0.3) 0px 19px 38px, rgba(0, 0, 0, 0.22) 0px 15px 12px;
    border-radius: 2px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.scrollable {
    overflow: auto;
}

.drop-down-left-align {
    border-top-left-radius: 0;
}

.drop-down-right-align {
    border-top-right-radius: 0;
}

.column-filter-form__header {
    display: flex;
    justify-content: end;
    gap: 6px;
}

.one-column-layout {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.two-column-layout {
    display: grid;
    grid-template-columns: 1fr 3fr;
    gap: 6px;
}

.three-column-layout {
    display: grid;
    grid-template-columns: 1fr 3fr 1fr;
    gap: 6px;
}

.category-form-layout {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr 15fr;
    column-gap: 9px;
}

.column-filter label {
    color: rgb(37, 36, 34);
    font-size: 14px;
    margin-bottom: 0;
}

.column-filter select {
    border: 1px solid rgb(118, 118, 118);
    border-radius: 2px;
    font-size: 14px;
    background-color: white;
    height: 28px;
}

.column-filter input {
    font-size: 14px;
    height: 28px;
    padding-left: 3px;
    padding-right: 2px;
}

.column-filter-form button {
    border: 1px solid rgb(118, 118, 118);
    border-radius: 2px;
}

.column-filter-button {
    border: none !important;
    background: none;
    color: rgb(118, 118, 118);
}

.column-filter button:focus,
.column-filter input:focus,
.column-filter select:focus {
    outline: rgba(102, 179, 227, 0.75) auto 1px !important;
    outline-offset: 0px;
    z-index: 99;
}

.column-filter a {
    font-family: 'Roboto';
}

.filter-active {
    color: black;
}
</style>
